import GridTable from "../../components/GridTable";
import {
  deleteUserAssignedAppraisalById,
  getAllUserAssignedAppraisal,
  filterAppraisals,
} from "../../api/appraisalApi";
import { showNotification } from "../../components/Notification";
import {
  updateMessage,
  resetSelectedAppraisalStates,
} from "../appraisal/appraisalSlice";
import FilterComponant from "../../components/FilterComponant";
import { getAllTemplates } from "../../api/templateApi";
import { getAllUsers } from "../../api/userApi";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getRatingNameFromValue,
  getStatusNameByValue,
  ratingOptions,
  reminderIntervalList,
  ROLES,
  statusOption,
} from "../../uitls/Utils";

import useAuth from "../../hooks/useAuth";

import Dropdown from "../../components/Dropdown";
import Loader from "../../components/Loader";
import DateInput from "../../components/DateInput";
import * as XLSX from "xlsx";

const AssignTemplate = () => {
  const { auth } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userAssignedAppraisal, message, error, loading } = useSelector(
    (state) => state.appraisalState
  );
  const filterButtonRef = React.useRef(null);

  // Function to format date strings to MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };

  const [filterVisible, setFilterVisible] = useState(false);
  const defaultFilterData = {
    templateTitle: "",
    employeeName: "",
    status: "",
    ratingName: "",
    dateFrom: null,
    dateTo: null,
  };
  const [filters, setFilters] = useState(defaultFilterData);
  const [filteredData, setFilteredData] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // State for templates and employees
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    dispatch(getAllUserAssignedAppraisal());
    dispatch(resetSelectedAppraisalStates());
    fetchTemplatesAndEmployees();
  }, [dispatch]);

  // Fetch templates and employees for filters
  const fetchTemplatesAndEmployees = async () => {
    try {
      const [templatesRes, employeesRes] = await Promise.all([
        dispatch(getAllTemplates()).unwrap(),
        dispatch(getAllUsers()).unwrap(),
      ]);

      // Transform templates to dropdown format
      const templateOptions =
        templatesRes?.map((template) => ({
          label: template.templateTitle,
          value: template.id,
        })) || [];

      // Transform employees to dropdown format
      const employeeOptions =
        employeesRes?.map((employee) => ({
          label: `${employee.firstName || ""} ${
            employee.lastName || ""
          }`.trim(),
          value: employee.id,
        })) || [];

      setTemplates(templateOptions);
      setEmployees(employeeOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("error", "Failed to load filter data");
    }
  };

  useEffect(() => {
    if (message && !error) {
      dispatch(getAllUserAssignedAppraisal());
      showNotification("success", message);
    } else if (!message && error) {
      showNotification("error", error);
    }
    setTimeout(() => {
      dispatch(updateMessage(""));
    }, 8000);
  }, [dispatch, error, message]);

  const startReviewing = (row) => {
    if (auth?.roleId === ROLES.admin) {
      navigate(
        "/admin/review-final-appraisal?id=" +
          row.id +
          "&action=REVIEW-FINAL-APPRAISAL"
      );
    }
  };
  const columns = [
    {
      field: "templateTitle",
      headerName: "Template Title",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span>{params.row.appraisalTemplate.templateTitle}</span>
      ),
    },

    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span>
          {params.row.assignedUser?.firstName || "-"}{" "}
          {params.row.assignedUser?.lastName || "-"}
        </span>
      ),
    },
    {
      field: "manager",
      headerName: "Manager Name",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span>{params.row.assignedUser.manager?.name}</span>
      ),
    },
    {
      field: "currentAppraisalStatus",
      headerName: "Status",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => <span>{getStatusNameByValue(params.value)}</span>,
    },
    {
      field: "performanceRating",
      headerName: "Rating",
      width: 150,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span>{getRatingNameFromValue(params.value)}</span>
      ),
    },
    {
      field: "userSubmissionEndDate",
      headerName: "User Submission End Date",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: "managerReviewEndDate",
      headerName: "Manager Review End Date",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },

    {
      field: "finalReviewEndDate",
      headerName: "Final Review End Date",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: "userReminderInterval",
      headerName: "User Email Interval",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params?.value ? (
          <span>
            {reminderIntervalList.find((e) => e.value === params?.value)?.label}
          </span>
        ) : (
          "N/A"
        ),
    },
    {
      field: "managerReminderInterval",
      headerName: "Manager Email Interval",
      width: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params?.value ? (
          <span>
            {reminderIntervalList.find((e) => e.value === params?.value)?.label}
          </span>
        ) : (
          "N/A"
        ),
    },
  ];
  const handleDelete = async (id, templateId) => {
    const result = await Swal.fire({
      titleText: "Delete Assign Template ?",
      text: "Are you sure want to delete this Assign Template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: {
        popup: "custom-swal-width", // Custom width of the popup
        icon: "custom-swal-icon", // Custom icon size
        title: "custom-swal-title", // Custom title size
        content: "custom-swal-content", // Custom content (text) size
      },
    });

    if (result.isConfirmed) {
      try {
        await dispatch(
          deleteUserAssignedAppraisalById({ id, templateId })
        ).unwrap();
        showNotification("success", "Assigned appraisal deleted successfully");
      } catch (error) {
        const errorMessage =
          error?.message || "Failed to delete assigned appraisal";
        console.error("Error deleting assigned appraisal:", error);
        showNotification("error", errorMessage);
      }
    }
  };
  useEffect(() => {
    // Only set initial data if no filters are active
    if (!isFilterActive) {
      // Sort by createdDate descending (newest first)
      const sortedData = Array.isArray(userAssignedAppraisal) 
        ? [...userAssignedAppraisal].sort((a, b) => {
            const dateA = new Date(a.createdDate || 0);
            const dateB = new Date(b.createdDate || 0);
            return dateB - dateA; // Descending order (newest first)
          })
        : [];
      setFilteredData(sortedData);
    }
  }, [userAssignedAppraisal, isFilterActive]);

  const applyFilter = async () => {
    const {
      templateTitle,
      employeeName,
      status,
      ratingName,
      dateFrom,
      dateTo,
    } = filters;

    // Prepare filter payload for API
    const filterPayload = {};

    // Add template title if selected
    if (templateTitle?.label) {
      filterPayload.templateTitle = templateTitle.label;
    }

    // Add employee name if selected
    if (employeeName?.label) {
      filterPayload.employeeName = employeeName.label;
    }

    // Add status if selected
    if (status?.label) {
      filterPayload.status = status.label;
    }

    // Add rating if selected
    if (ratingName?.label) {
      filterPayload.rating = ratingName.label;
    }

    // Add date range if selected
    if (dateFrom) {
      // Format date without timezone conversion
      const year = dateFrom.getFullYear();
      const month = String(dateFrom.getMonth() + 1).padStart(2, "0");
      const day = String(dateFrom.getDate()).padStart(2, "0");
      filterPayload.createdDate = `${year}-${month}-${day}`;
    }

    if (dateTo) {
      // Format date without timezone conversion
      const year = dateTo.getFullYear();
      const month = String(dateTo.getMonth() + 1).padStart(2, "0");
      const day = String(dateTo.getDate()).padStart(2, "0");
      filterPayload.updatedDate = `${year}-${month}-${day}`;
    }

    try {
      // Call the filter API
      const filteredResults = await dispatch(
        filterAppraisals(filterPayload)
      ).unwrap();

      // Sort filtered results by createdDate descending (newest first)
      const sortedResults = Array.isArray(filteredResults)
        ? [...filteredResults].sort((a, b) => {
            const dateA = new Date(a.createdDate || 0);
            const dateB = new Date(b.createdDate || 0);
            return dateB - dateA; // Descending order (newest first)
          })
        : [];

      setFilteredData(sortedResults);
      setIsFilterActive(true);
      setFilterVisible(false);

      showNotification("success", "Filters applied successfully");
    } catch (error) {
      console.error("Error applying filters:", error);
      showNotification("error", "Failed to apply filters");
    }
  };

  const resetFilter = () => {
    setFilters(defaultFilterData);
    // Sort by createdDate descending (newest first) when resetting
    const sortedData = Array.isArray(userAssignedAppraisal) 
      ? [...userAssignedAppraisal].sort((a, b) => {
          const dateA = new Date(a.createdDate || 0);
          const dateB = new Date(b.createdDate || 0);
          return dateB - dateA; // Descending order (newest first)
        })
      : [];
    setFilteredData(sortedData);
    setIsFilterActive(false);
  };

  const isApplyButtonDisabled = () => {
    const {
      templateTitle,
      ratingName,
      employeeName,
      status,
      dateFrom,
      dateTo,
    } = filters;
    return !(
      templateTitle ||
      employeeName ||
      status ||
      ratingName ||
      dateFrom ||
      dateTo
    );
  };
  const onClickAction = (actionType, row, id = "") => {
    if (actionType === "View" || actionType === "Review") {
      startReviewing(row);
    } else if (actionType === "EDIT" || actionType === "VIEW") {
      navigate(
        "/admin/assign-template/add-update?id=" + id + "&action=" + actionType
      );
    } else if (actionType === "DELETE") {
      handleDelete(id, row?.appraisalTemplate.id);
    }
  };

  const handleDropdownChange = (name) => (value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };
  const closeFilterModal = () => {
    setFilterVisible(false);
  };

  // Export filtered data to Excel
  const handleExportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      showNotification("error", "No data to export");
      return;
    }

    try {
      // Debug: Log the first item to see the data structure
      console.log("Export data sample:", filteredData[0]);

      // Prepare data for export
      const exportData = filteredData.map((item) => {
        // Debug: Log status and rating values
        const statusValue = getStatusNameByValue(item.currentAppraisalStatus);
        const ratingValue = getRatingNameFromValue(item.performanceRating);
        console.log("Status:", item.currentAppraisalStatus, "->", statusValue);
        console.log("Rating:", item.performanceRating, "->", ratingValue);

        // Extract text from React elements or use raw values
        const getTextFromReactElement = (element) => {
          if (typeof element === "string") return element;
          if (element?.props?.children) {
            return element.props.children;
          }
          return null;
        };

        const statusText =
          getTextFromReactElement(statusValue) ||
          item.currentAppraisalStatus ||
          "N/A";
        const ratingText =
          getTextFromReactElement(ratingValue) ||
          item.performanceRating ||
          "N/A";

        console.log("Final Status Text:", statusText);
        console.log("Final Rating Text:", ratingText);

        return {
          "Template Title": item.appraisalTemplate?.templateTitle || "N/A",
          "Employee Name":
            `${item.assignedUser?.firstName || ""} ${
              item.assignedUser?.lastName || ""
            }`.trim() || "N/A",
          "Manager Name": item.assignedUser?.manager?.name || "N/A",
          Status: statusText,
          Rating: ratingText,
          "User Submission End Date": formatDate(item.userSubmissionEndDate),
          "Manager Review End Date": formatDate(item.managerReviewEndDate),
          "Final Review End Date": formatDate(item.finalReviewEndDate),
          "User Email Interval": item.userReminderInterval
            ? reminderIntervalList.find(
                (e) => e.value === item.userReminderInterval
              )?.label || "N/A"
            : "N/A",
          "Manager Email Interval": item.managerReminderInterval
            ? reminderIntervalList.find(
                (e) => e.value === item.managerReminderInterval
              )?.label || "N/A"
            : "N/A",
        };
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Template Title
        { wch: 20 }, // Employee Name
        { wch: 20 }, // Manager Name
        { wch: 15 }, // Status
        { wch: 15 }, // Rating
        { wch: 25 }, // User Submission End Date
        { wch: 25 }, // Manager Review End Date
        { wch: 25 }, // Final Review End Date
        { wch: 20 }, // User Email Interval
        { wch: 20 }, // Manager Email Interval
      ];
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Appraisal Data");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `appraisal_data_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      showNotification("success", "Data exported successfully");
    } catch (error) {
      console.error("Error exporting data:", error);
      showNotification("error", "Failed to export data");
    }
  };
  return (
    <div className="pt-4 md:pt-6 2xl:pt-10 w-full px-4 md:px-6 lg:px-8">
      {loading && <Loader />}
      <GridTable
        rowData={filteredData}
        columnData={columns}
        onClickAction={onClickAction}
        actions={["DELETE", "EDIT", "APPRAISAL_REVIEW"]}
        onClickAdd={() => navigate("/admin/assign-template/add-update")}
        onClickFilter={() => {
          setFilterVisible(true);
        }}
        filterButtonTitle="Filters"
        isFilterAdded={true}
        isFilterActive={isFilterActive}
        showExportButton={true}
        onExport={handleExportToExcel}
        filterButtonRef={filterButtonRef}
      />

      {filterVisible && (
        <FilterComponant
          title="Apply Filters"
          width="w-[420px]"
          onClose={closeFilterModal}
          anchorEl={filterButtonRef}
          position="bottom-right"
          modalBody={
            <div className="space-y-6">
              <div className="space-y-3">
                <label
                  htmlFor="template-title-dropdown"
                  className="block text-sm font-bold text-gray-700 uppercase tracking-wide"
                >
                  Template Title
                </label>
                <Dropdown
                  id="template-title-dropdown"
                  submitRef={true}
                  defaultValue={filters?.templateTitle}
                  options={templates}
                  handleChange={handleDropdownChange("templateTitle")}
                  defaultLabel="Search and select template"
                  label=""
                  className="h-[48px]"
                  menuPlacement="bottom"
                  isClearable={true}
                />
              </div>

              <div className="space-y-3">
                <label
                  htmlFor="employee-name-dropdown"
                  className="block text-sm font-bold text-gray-700 uppercase tracking-wide"
                >
                  Employee Name
                </label>
                <Dropdown
                  id="employee-name-dropdown"
                  submitRef={true}
                  defaultValue={filters?.employeeName}
                  options={employees}
                  handleChange={handleDropdownChange("employeeName")}
                  defaultLabel="Search and select employee"
                  label=""
                  className="h-[48px]"
                  menuPlacement="bottom"
                  isClearable={true}
                />
              </div>

              <div className="space-y-3">
                <label
                  htmlFor="status-dropdown"
                  className="block text-sm font-bold text-gray-700 uppercase tracking-wide"
                >
                  Status
                </label>
                <Dropdown
                  id="status-dropdown"
                  submitRef={true}
                  defaultValue={filters?.status}
                  options={statusOption}
                  handleChange={handleDropdownChange("status")}
                  defaultLabel="Select Status"
                  label=""
                  className="h-[48px]"
                  menuPlacement="bottom"
                  isClearable={true}
                />
              </div>

              <div className="space-y-3 !mt-3">
                <label
                  htmlFor="rating-dropdown"
                  className="block text-sm font-bold text-gray-700 uppercase tracking-wide"
                >
                  Rating
                </label>
                <Dropdown
                  id="rating-dropdown"
                  submitRef={true}
                  defaultValue={filters?.ratingName}
                  options={ratingOptions}
                  handleChange={handleDropdownChange("ratingName")}
                  defaultLabel="Select Rating"
                  label=""
                  className="h-[48px]"
                  menuPlacement="bottom"
                  isClearable={true}
                />
              </div>

              <div className="space-y-3 !mt-3">
                <label
                  htmlFor="date-range-container"
                  className="block text-sm font-bold text-gray-700 uppercase tracking-wide"
                >
                  Date Range
                </label>
                <div
                  id="date-range-container"
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="space-y-2">
                    <label
                      htmlFor="date-from-input"
                      className="block text-xs font-medium text-gray-600"
                    >
                      From Date
                    </label>
                    <DateInput
                      id="date-from-input"
                      selected={filters.dateFrom}
                      onChange={(date) =>
                        setFilters({ ...filters, dateFrom: date })
                      }
                      placeholderText="mm/dd/yyyy"
                      dateFormat="MM/dd/yyyy"
                      maxDate={filters.dateTo || undefined}
                      className="w-full px-4 py-3 border-2 border-[#D3D3D3] rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="date-to-input"
                      className="block text-xs font-medium text-gray-600"
                    >
                      To Date
                    </label>
                    <DateInput
                      id="date-to-input"
                      selected={filters.dateTo}
                      onChange={(date) =>
                        setFilters({ ...filters, dateTo: date })
                      }
                      placeholderText="mm/dd/yyyy"
                      dateFormat="MM/dd/yyyy"
                      minDate={filters.dateFrom || undefined}
                      className="w-full px-4 py-3 border-2 border-[#D3D3D3] rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          }
          footer={
            <>
              <button
                onClick={resetFilter}
                className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:border-gray-400 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10">Reset</span>
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-in-out bg-gradient-to-r from-transparent via-white/30 to-transparent"></span>
              </button>
              <button
                onClick={applyFilter}
                className={`px-6 py-2.5 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:from-[#135e96] hover:to-[#0b3a5c] relative overflow-hidden group ${
                  isApplyButtonDisabled() ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={isApplyButtonDisabled()}
              >
                <span className="relative z-10">Apply Filters</span>
                {!isApplyButtonDisabled() && (
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
                )}
              </button>
            </>
          }
        />
      )}
    </div>
  );
};

export default AssignTemplate;
